import { Component, State, Prop, Method, EventEmitter, Event, Host, h } from '@stencil/core';
// import dayjs from 'dayjs';
import { DateHelper } from './date.helper';
import { IDatePickerModel } from './models/date-picker.model';

@Component({
  tag: 'pnk-datepicker',
  styleUrl: ' pnk-datepicker.css',
  shadow: false,
})
export class PnkDatePicker {
  @Prop() width: number = 300; // datepicker width

  @Prop() date: Date = new Date();

  @Prop() open: boolean = false;

  @Prop() from?: number; // prop used to select the years range we want to set the datepicker

  @Prop() to?: number; // prop used to select the years range we want to set the datepicker

  @Prop() cancelLabel?: string;

  @Prop() okLabel?: string;

  @Prop() language?: string;

  @Prop() openUnder: boolean = true; // gives the opportunity to choose where to open the datepicker calendar.

  @Prop() disable: boolean = false;

  //  True= calendar opened under the navbar, False: calendar open above the navbar
  @Prop() dateChange!: (date: Date) => void;

  @State() currentDay: Date = new Date();

  @State() currentMonth: Date = new Date();

  @State() showContent: boolean = false;

  @State() datepickerDates: IDatePickerModel[] = [];

  @Event() dateChanged!: EventEmitter; // check if the date has changed

  componentWillLoad() {
    this.init();
    this.getDays(this.date);
  }

  // return a promise that gives the date selected in the datepicker
  @Method()
  public async getDate(): Promise<Date> {
    return await Promise.resolve(this.date);
  }

  render() {
    return (
      <Host style={{ 'max-width': `${this.width}px`, 'position': 'relative' }}>
        <div class="pnk-w-[220px]">
          {this.showContent && this.disable === false && this.openUnder === false && (
            <div class="pnk-mb-[5px] pnk-bg-disabled-base pnk-rounded pnk-p-[5px]">
              <div class="pnk-bg-white pnk-rounded">
                <div class="pnk-mb-[5px]">
                  <pnk-datepicker-header currentDay={this.currentDay} yearFrom={this.from} yearTo={this.to} dateChange={date => this.setCurrentDate(date)}></pnk-datepicker-header>
                </div>
              </div>
              <div class="pnk-bg-white pnk-rounded">
                <pnk-datepicker-inner
                  language={this.language}
                  itemSize={this.width / 8}
                  datepickerDates={this.datepickerDates}
                  currentMonth={this.currentMonth}
                  resolveDayView={(d: IDatePickerModel) => this.resolveDayView(d)}
                  dateSelect={(date: Date) => this.setCurrentDate(date)}
                  monthChange={(date: Date) => this.getDays(date)}
                  approve={() => this.approve()}
                ></pnk-datepicker-inner>
              </div>
            </div>
          )}
          <pnk-datepicker-topnav
            disabled={this.disable}
            date={this.date}
            dateChange={(date: Date) => this.handleTopNavigationChange(date)} // this.handleTopNavigationChange(date)
            toggleView={() => this.toggleView()}
          ></pnk-datepicker-topnav>
          {this.showContent && this.openUnder === true && this.disable === false && (
            <div class="pnk-mt-[5px] pnk-bg-disabled-base pnk-rounded pnk-p-[5px]">
              <div class="pnk-bg-white pnk-rounded">
                <div class="pnk-mb-[5px]">
                  <pnk-datepicker-header
                    language={this.language}
                    currentDay={this.currentDay}
                    yearFrom={this.from}
                    yearTo={this.to}
                    dateChange={date => this.setCurrentDate(date)}
                  ></pnk-datepicker-header>
                </div>
              </div>
              <div class="pnk-bg-white pnk-rounded">
                <pnk-datepicker-inner
                  language={this.language}
                  itemSize={this.width / 8}
                  datepickerDates={this.datepickerDates}
                  currentMonth={this.currentMonth}
                  resolveDayView={(d: IDatePickerModel) => this.resolveDayView(d)}
                  dateSelect={(date: Date) => this.setCurrentDate(date)}
                  monthChange={(date: Date) => this.getDays(date)}
                  approve={() => this.approve()}
                ></pnk-datepicker-inner>
              </div>
            </div>
          )}
        </div>
      </Host>
    );
  }

  // if the day is selected has purple background and white text
  private resolveDayView(d: IDatePickerModel): string {
    let className = '';

    if (DateHelper.areDatesEqual(d.date, this.currentDay) && d.isCurrentMonth && d.text) {
      className = ' pnk-cursor-pointer pnk-bg-accent pnk-text-white pnk-rounded-full pnk-w-[30px] pnk-h-[30px] pnk-flex pnk-justify-center pnk-items-center';
    }

    if (d.isCurrentMonth && !DateHelper.areDatesEqual(d.date, this.currentDay)) {
      className +=
        'pnk-cursor-pointer hover:pnk-cursor-pointer hover:pnk-bg-primary-base active:pnk-bg-accent pnk-rounded-full pnk-h-[30px] pnk-w-[30px] pnk-flex pnk-text-center pnk-justify-center pnk-items-center';
    }
    return <span class={className}>{d.isCurrentMonth && d.text}</span>;
  }

  private init() {
    if (this.language) {
      DateHelper.setLocale(this.language);
    }
    this.showContent = this.open;
    this.date = new Date(this.date || new Date());
    this.currentDay = this.date;
    this.currentMonth = this.date;
  }

  // set current day as today
  private setCurrentDate(date?: Date) {
    if (date) {
      this.currentDay = new Date(date);
    }
    this.getDays(this.currentDay);
    return this.currentDay;
  }

  private handleTopNavigationChange(date: Date) {
    this.setCurrentDate(date);
    this.approve(false);
  }

  // handles calendar opening or closing
  private toggleView(): void {
    this.showContent = !this.showContent;
  }

  // handle click on a day and emits a new date
  private approve(toggle = true): void {
    this.date = this.currentDay;
    this.getDays(this.date);
    this.dateChanged.emit(this.date);
    if (toggle) {
      this.toggleView();
    }
    if (this.dateChange) {
      this.dateChange(new Date(this.date));
    }
  }

  private getDays(date: Date): void {
    this.currentMonth = date;
    this.datepickerDates = [...DateHelper.getMonthDates(date, this.language || 'it')];
  }
}
